/**
 * All environment-driven configuration in one place, matching `pos-pwa/src/env.ts`'s
 * convention. Every value has a sane local-dev default so `pnpm dev` works against
 * `backend/`'s docker-compose stack (see `backend/BACKEND_ARCHITECTURE.md`'s "Running it
 * locally") without any `.env` file.
 */

/** Base URL of the backend's versioned API — already includes `/api/v1`. */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000/api/v1";

/** Raw backend root (no `/api/v1` suffix) — needed to resolve product image
 * paths (`ProductImageOut.thumb_path` etc. are root-relative, e.g.
 * `/img/{tenant_id}/...`, per `backend/app/services/image_service.py::public_image_path`). */
export const API_ROOT_URL: string = import.meta.env.VITE_API_ROOT_URL ?? "http://localhost:8000";

/** Cross-app SSO link target — `ecommerce-store` is still a genuinely separate app/
 * origin (see `src/components/CrossAppLinks.tsx` and `backend/app/services/sso_service.py`'s
 * handoff design). POS is no longer a separate app as of the `pos-pwa` merge — `/pos/*` is
 * an in-app route now, so there is no `POS_URL` to redirect to anymore. */
export const ECOMMERCE_URL: string = import.meta.env.VITE_ECOMMERCE_URL ?? "http://localhost:8103";

/**
 * `/pos/*`-only configuration, ported from `pos-pwa/src/env.ts` as part of that app's
 * merge into this one — same defaults, same meaning, just living alongside the
 * back-office config above now instead of in a separate app.
 */

/** Origin that serves product image bytes (self-hosted nginx/MinIO, per
 * system_architecture.md §7.1). Defaults to the API's own origin; override via
 * `VITE_IMAGE_BASE_URL` if images are served from a distinct host/port. */
export const IMAGE_BASE_URL: string = import.meta.env.VITE_IMAGE_BASE_URL ?? new URL(API_BASE_URL).origin;

/** Network/IP printer relay — see `src/pos/hardware/printer/networkPrinterClient.ts`. */
export const NETWORK_PRINTER_RELAY_PATH = "/print/receipt";

/** Sync loop tick interval while online (ms), plus reacting to `online`/`offline` browser
 * events immediately (system_architecture.md §3.2). */
export const SYNC_TICK_INTERVAL_MS = 20_000;

/** Exponential-backoff-with-jitter bounds for the sync loop (§3.2, §8's resilience
 * matrix: "max interval capped, e.g. 60s"). */
export const SYNC_BACKOFF_BASE_MS = 2_000;
export const SYNC_BACKOFF_MAX_MS = 60_000;
